import React, { ReactNode } from 'react';

import {
    AccessRightModel,
    ActivityForm,
    AgentTargetForm,
    BirthWishForm,
    ChallengeDetail,
    ChallengeLayout,
    ContactForm,
    DocumentCategorySelector,
    DocumentForm,
    LeaseForm,
    MandateForm,
    MatchingForm,
    NeedForm,
    PermissionForm,
    PermissionModel,
    PlanningForm,
    PropertyForm,
    PublicationForm,
    TaskForm,
    TransactionForm,
    useActivityDetail,
    useActivityForm,
    useActivityTableConfig,
    useAgentTargetDetail,
    useAgentTargetForm,
    useAgentTargetTableConfig,
    useBirthWishDetail,
    useBirthWishForm,
    useBirthWishTableConfig,
    useContactDetail,
    useContactForm,
    useContactTableConfig,
    useDocumentDetail,
    useDocumentForm,
    useDocumentTableConfig,
    useLeaseDetail,
    useLeaseForm,
    useLeaseTableConfig,
    useMandateDetail,
    useMandateForm,
    useMandateTableConfig,
    useMatchingDetail,
    useMatchingForm,
    useMatchingTableConfig,
    useNeedDetail,
    useNeedForm,
    useNeedTableConfig,
    usePermissionDetail,
    usePermissionForm,
    usePermissionTableConfig,
    usePlanningDetail,
    usePlanningForm,
    usePlanningTableConfig,
    usePropertyDetail,
    usePropertyForm,
    usePropertyTableConfig,
    usePublicationDetail,
    usePublicationForm,
    usePublicationTableConfig,
    UserForm,
    useTaskDetail,
    useTaskForm,
    useTaskTableConfig,
    useTransactionDetail,
    useTransactionForm,
    useTransactionTableConfig,
    useUserDetail,
    useUserForm,
    useUserTableConfig
} from '@/Domains';

import {
    FaCubes,
    FaFileInvoice,
    FaTasks,
    FaHandshake,
    FaFolder,
    FaBirthdayCake,
    FaFilter,
    FaFileInvoiceDollar,
    FaBuilding,
    FaBoxOpen,
    FaHive,
    FaBox,
    FaCalendarAlt,
    FaFunnelDollar,
    FaUserLock,
    FaBullhorn,
    FaUsers,
    FaBullseye,
} from '@/Shared/Lib';

import { USER_ROLES } from '../Helpers';
import ResultLayout from '@/Shared/UI/Layouts/ResultLayout/ResultLayout';
import { DOMAIN_TYPES, DomainType } from '../Types/ui-types';
import { ModalHook } from '../Logic';
import { DomainFormProps, EntityHook, UIFeedbackHooks } from '../Types';

export interface DomainHookProps {
    accessRight: AccessRightModel,
    uiFeedbackHooks: UIFeedbackHooks,
    inModalHook?: ModalHook,
    extraArgs?: Record<string, any>,
    permissionHook : EntityHook<PermissionModel>
}

export type DomainHook = (props : DomainHookProps) => any;

export interface DomainConfig {
    domainType: DomainType;
    domainLabel: string;
    domainLabelPl: string;
    DomainIcon: React.ElementType;
    domainRoute: string;
    requireRole: USER_ROLES;
    useTableConfig?: DomainHook;
    useDetailHook?: DomainHook;
    useFormHook?: DomainHook;
    hasFormSubType?: boolean;
    DomainForm?: React.ComponentType<DomainFormProps>;
    DomainFormSelector? : React.ElementType;
    layout?: React.ElementType;   // <- layout spécifique
    detail?: React.ElementType;   // <- détail spécifique ou generic
    globalButtons: DOMAIN_TYPES[];
    contextualButtons? : DOMAIN_TYPES[];
    desableDelete?: boolean;
    prohibitCreate? : boolean;
    desableBadge?: boolean;
    noDisplayMenu?: boolean;
}


/**
 * =====================================================
 * DOMAIN_CONFIGS
 * =====================================================
 *
 * Central configuration registry for all domain types
 * in the application.
 *
 * Responsibilities:
 * - Defines labels, icons, and routes for each domain type.
 * - Connects each domain to its table config, detail hook, and form component.
 * - Specifies user roles required to access each domain.
 * - Lists the domains allowed as global buttons (outside context).
 * - Lists the domains allowed as contextual buttons (inside context).
 * - Optionally defines custom layouts or detail components.
 *
 * Notes:
 * - This is a declarative configuration; no logic or hooks are executed here.
 * - Contextual buttons are generated dynamically in the detail hook for each domain.
 *
 * Author: Samano CASTRE
 * Date: February 28, 2026
 */
export const DOMAIN_CONFIGS: Record<DomainType, DomainConfig> = {

    
    Contact: {
        domainType: DOMAIN_TYPES.Contact,
        domainLabel: 'Contact',
        domainLabelPl: 'Contacts',
        DomainIcon: FaHandshake,
        useTableConfig: useContactTableConfig,
        domainRoute: '/contacts',
        useDetailHook: useContactDetail,
        useFormHook: useContactForm,
        requireRole: USER_ROLES.USER,
        DomainForm: ContactForm,

        // Actions outside current contact context
        globalButtons: [
            DOMAIN_TYPES.Contact,
            DOMAIN_TYPES.Need,
            DOMAIN_TYPES.Planning
        ] as DOMAIN_TYPES[],

        // Actions staying in contact context
        contextualButtons : [
            DOMAIN_TYPES.Need,
            DOMAIN_TYPES.Property
        ] as DOMAIN_TYPES[]
    },
    Mandate: {
        domainType: DOMAIN_TYPES.Mandate,
        domainLabel: 'Mandat',
        domainLabelPl: 'Mandats',
        DomainIcon: FaFileInvoice,
        useTableConfig: useMandateTableConfig,
        domainRoute: '/mandates',
        useDetailHook: useMandateDetail,
        requireRole: USER_ROLES.USER,
        DomainForm: MandateForm,
        useFormHook: useMandateForm,
        globalButtons: [
            DOMAIN_TYPES.Mandate,
            DOMAIN_TYPES.Lease,
            DOMAIN_TYPES.Document
        ] as DOMAIN_TYPES[],

        contextualButtons : [
            DOMAIN_TYPES.Transaction
        ] as DOMAIN_TYPES[]
    },
    
    Property: {
        domainType: DOMAIN_TYPES.Property,
        domainLabel: 'Bien',
        domainLabelPl: 'Biens',
        DomainIcon: FaBuilding,
        useTableConfig: usePropertyTableConfig,
        domainRoute: '/properties',
        useDetailHook: usePropertyDetail,
        requireRole: USER_ROLES.USER,
        DomainForm: PropertyForm,
        useFormHook: usePropertyForm,
        globalButtons: [
            DOMAIN_TYPES.Property,
            DOMAIN_TYPES.Need,
            DOMAIN_TYPES.Mandate
        ] as DOMAIN_TYPES[],
        contextualButtons : [
            DOMAIN_TYPES.Mandate,
            DOMAIN_TYPES.Publication
        ] as DOMAIN_TYPES[]
    },
    Publication: {
        domainType: DOMAIN_TYPES.Publication,
        domainLabel: 'Annonce',
        domainLabelPl: 'Annonces',
        DomainIcon: FaBullhorn,
        useTableConfig: usePublicationTableConfig,
        domainRoute: '/publications',
        useDetailHook: usePublicationDetail,
        DomainForm: PublicationForm,
        useFormHook: usePublicationForm,
        requireRole: USER_ROLES.USER,
        globalButtons: [
            DOMAIN_TYPES.Publication,
            DOMAIN_TYPES.Property,
            DOMAIN_TYPES.Need
        ] as DOMAIN_TYPES[]
    },
    Need: {
        domainType: DOMAIN_TYPES.Need,
        domainLabel: 'Besoin',
        domainLabelPl: 'Besoins',
        DomainIcon: FaBoxOpen,
        useTableConfig: useNeedTableConfig,
        domainRoute: '/needs',
        useDetailHook: useNeedDetail,
        requireRole: USER_ROLES.USER,
        DomainForm: NeedForm,
        useFormHook: useNeedForm,
        globalButtons: [
            DOMAIN_TYPES.Need,
            DOMAIN_TYPES.Property,
            DOMAIN_TYPES.Transaction
        ] as DOMAIN_TYPES[],
        contextualButtons : [
            DOMAIN_TYPES.Mandate
        ] as DOMAIN_TYPES[]
    },
    Matching: {
        domainType: DOMAIN_TYPES.Matching,
        domainLabelPl: 'Rapprochements',
        domainLabel: 'Rapprochement',
        DomainIcon: FaHive,
        useTableConfig: useMatchingTableConfig,
        domainRoute: '/matchings',
        useDetailHook: useMatchingDetail,
        requireRole: USER_ROLES.USER,
        DomainForm: MatchingForm,
        useFormHook: useMatchingForm,
        globalButtons: [
            DOMAIN_TYPES.Need,
            DOMAIN_TYPES.Property,
            DOMAIN_TYPES.Contact
        ] as DOMAIN_TYPES[],
        desableDelete: true,
        prohibitCreate : true
    },
    
    AgentTarget: {
        domainType: DOMAIN_TYPES.AgentTarget,
        domainLabel: 'Objectif Agent',
        domainLabelPl: 'Objectifs Agents',
        DomainIcon: FaBullseye,
        useTableConfig: useAgentTargetTableConfig,
        domainRoute: '/agenttargets',
        useDetailHook: useAgentTargetDetail,
        useFormHook : useAgentTargetForm,
         DomainForm: AgentTargetForm,
        requireRole: USER_ROLES.USER,
        globalButtons: [
            DOMAIN_TYPES.AgentTarget,
            DOMAIN_TYPES.Property,
            DOMAIN_TYPES.Need
        ] as DOMAIN_TYPES[]
    },
    Transaction: {
        domainType: DOMAIN_TYPES.Transaction,
        domainLabel: 'Transaction',
        domainLabelPl: 'Transactions',
        DomainIcon: FaBox,
        useTableConfig: useTransactionTableConfig,
        domainRoute: '/transactions',
        useDetailHook: useTransactionDetail,
        requireRole: USER_ROLES.USER,
        DomainForm: TransactionForm,
        useFormHook: useTransactionForm,
        globalButtons: [
            DOMAIN_TYPES.Transaction,
            DOMAIN_TYPES.Mandate,
            DOMAIN_TYPES.Property
        ] as DOMAIN_TYPES[],

        contextualButtons : [
            DOMAIN_TYPES.Lease
        ] as DOMAIN_TYPES[]
    },
    Lease: {
        domainType: DOMAIN_TYPES.Lease,
        domainLabel: 'Bail',
        domainLabelPl: 'Baux',
        DomainIcon: FaFileInvoiceDollar,
        useTableConfig: useLeaseTableConfig,
        domainRoute: '/leases',
        useDetailHook: useLeaseDetail,
        requireRole: USER_ROLES.USER,
        DomainForm: LeaseForm,
        useFormHook: useLeaseForm,
        globalButtons: [
            DOMAIN_TYPES.Lease,
            DOMAIN_TYPES.Mandate,
            DOMAIN_TYPES.Transaction
        ] as DOMAIN_TYPES[]
    },
    Activity: {
        domainType: DOMAIN_TYPES.Activity,
        domainLabelPl: 'Activités',
        domainLabel: 'Activité',
        DomainIcon: FaCubes,
        useTableConfig: useActivityTableConfig,
        domainRoute: '/activities',
        useDetailHook: useActivityDetail,
        requireRole: USER_ROLES.USER,
        DomainForm: ActivityForm,
        useFormHook: useActivityForm,
        globalButtons: [
            DOMAIN_TYPES.Activity,
            DOMAIN_TYPES.Need,
            DOMAIN_TYPES.Mandate
        ] as DOMAIN_TYPES[]
    },


    Planning: {
        domainType: DOMAIN_TYPES.Planning,
        domainLabel: 'Planning',
        domainLabelPl: 'Plannings',
        DomainIcon: FaCalendarAlt,
        useTableConfig: usePlanningTableConfig,
        domainRoute: '/plannings',
        useDetailHook: usePlanningDetail,
        requireRole: USER_ROLES.USER,
        DomainForm: PlanningForm,
        useFormHook: usePlanningForm,
        globalButtons: [
            DOMAIN_TYPES.Planning,
            DOMAIN_TYPES.Activity,
            DOMAIN_TYPES.Task
        ] as DOMAIN_TYPES[]
    },
    Result: {
        domainType: DOMAIN_TYPES.Result,
        domainLabel: 'Résultat',
        domainLabelPl: 'Résultats',
        DomainIcon: FaFunnelDollar,
        domainRoute: '/results',
        requireRole: USER_ROLES.USER,
        layout: ResultLayout,

        globalButtons: [
            DOMAIN_TYPES.Mandate,
            DOMAIN_TYPES.Lease,
            DOMAIN_TYPES.Property
        ] as DOMAIN_TYPES[],
        desableBadge: true
    },

    Task: {
        domainType: DOMAIN_TYPES.Task,
        domainLabel: 'Tâche',
        domainLabelPl: 'Tâches',
        DomainIcon: FaTasks,
        useTableConfig: useTaskTableConfig,
        domainRoute: '/tasks',
        useDetailHook: useTaskDetail,
        requireRole: USER_ROLES.USER,
        DomainForm: TaskForm,
        useFormHook: useTaskForm,
        globalButtons: [
            DOMAIN_TYPES.Task,
            DOMAIN_TYPES.Activity,
            DOMAIN_TYPES.Need
        ] as DOMAIN_TYPES[],
        contextualButtons : [
            DOMAIN_TYPES.Activity
        ] as DOMAIN_TYPES[]
    },
    Document: {
        domainType: DOMAIN_TYPES.Document,
        domainLabel: 'Document',
        domainLabelPl: 'Documents',
        DomainIcon: FaFolder,
        useTableConfig: useDocumentTableConfig,
        domainRoute: '/documents',
        useDetailHook: useDocumentDetail,
        requireRole: USER_ROLES.USER,
        hasFormSubType: true,
        DomainForm: DocumentForm,
        DomainFormSelector : DocumentCategorySelector,
        useFormHook: useDocumentForm,
        globalButtons: [
            DOMAIN_TYPES.Document,
            DOMAIN_TYPES.Need,
            DOMAIN_TYPES.Activity
        ] as DOMAIN_TYPES[]
    },

    User: {
        domainType: DOMAIN_TYPES.User,
        domainLabel: 'Agent',
        domainLabelPl: 'Agents',
        DomainIcon: FaUsers,
        useTableConfig: useUserTableConfig,
        domainRoute: '/users',
        useDetailHook: useUserDetail,
        requireRole: USER_ROLES.SUPER_ADMIN,
        DomainForm: UserForm,
        useFormHook: useUserForm,
        globalButtons: [
            DOMAIN_TYPES.User,
            DOMAIN_TYPES.Contact,
            DOMAIN_TYPES.Document
        ] as DOMAIN_TYPES[],

        contextualButtons : [
            DOMAIN_TYPES.AgentTarget
        ] as DOMAIN_TYPES[],
        desableBadge: true
    },
    BirthWish: {
        domainType: DOMAIN_TYPES.BirthWish,
        domainLabel: 'Anniversaire',
        domainLabelPl: 'Anniversaires',
        DomainIcon: FaBirthdayCake,
        useTableConfig: useBirthWishTableConfig,
        domainRoute: '/birthwishes',
        useDetailHook: useBirthWishDetail,
        requireRole: USER_ROLES.ADMIN,
        DomainForm: BirthWishForm,
        useFormHook: useBirthWishForm,
        prohibitCreate : true,
        globalButtons: [
            DOMAIN_TYPES.Contact,
            DOMAIN_TYPES.User,
            DOMAIN_TYPES.Document
        ] as DOMAIN_TYPES[]
    },

    Permission: {
        domainType: DOMAIN_TYPES.Permission,
        domainLabel: 'Permission',
        domainLabelPl: 'Permissions',
        DomainIcon: FaUserLock,
        useTableConfig: usePermissionTableConfig,
        domainRoute: '/permissions',
        useDetailHook: usePermissionDetail,
        requireRole: USER_ROLES.SUPER_ADMIN,
        DomainForm: PermissionForm,
        useFormHook: usePermissionForm,
        globalButtons: [
            DOMAIN_TYPES.User,
            DOMAIN_TYPES.Contact,
            DOMAIN_TYPES.Document,
        ] as DOMAIN_TYPES[],
        desableDelete: true,
        prohibitCreate : true
    },

    Challenge: {
        domainType: DOMAIN_TYPES.Challenge,
        domainLabel: 'Challenge',
        domainLabelPl: 'Challenges',
        DomainIcon: FaFilter,
        domainRoute: '/challenges',
        requireRole: USER_ROLES.ADMIN,
        layout: ChallengeLayout,
        detail: ChallengeDetail,
        globalButtons: [
            DOMAIN_TYPES.Contact,
            DOMAIN_TYPES.User,
            DOMAIN_TYPES.Permission
        ] as DOMAIN_TYPES[],
        desableBadge: true,
        noDisplayMenu: true
    }
} as const;